import { useForm } from '@mantine/form';
import useChargingServiceImages from '../../../hooks/charging-services/useChargingServiceImages.ts';
import useAddChargingServiceImage from '../../../hooks/charging-services/useAddChargingServiceImage.ts';
import useRemoveChargingServiceImage from '../../../hooks/charging-services/useRemoveChargingServiceImage.ts';
import {
  Button,
  Flex,
  Group,
  Image,
  LoadingOverlay,
  Stack,
  Text,
} from '@mantine/core';
import {
  IconCloudUpload,
  IconPlus,
  IconUpload,
  IconX,
} from '@tabler/icons-react';
import { Dropzone, FileWithPath, MIME_TYPES } from '@mantine/dropzone';
import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useChargingService from '../../../hooks/charging-services/useChargingService.ts';
import {
  AddChargingServiceImageRequest,
  RemoveChargingServiceImageRequest,
} from '../../../api/requests/charging-service-request.ts';
import { notifyError } from '../../../utils/NotifyError.tsx';

export interface MutateChargingServiceImagesFormValues {
  chargingServiceId: string;
}

interface MutateChargingServiceImagesFormProps {
  initialValues: MutateChargingServiceImagesFormValues;
  onSubmit: (formValues: MutateChargingServiceImagesFormValues) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  submitButtonText?: string;
  isUpdating: boolean;
}

export default function MutateChargingServiceImagesForm({
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting,
  isUpdating,
}: MutateChargingServiceImagesFormProps) {
  const form = useForm<MutateChargingServiceImagesFormValues>({
    mode: 'controlled',
    initialValues: {
      chargingServiceId: initialValues.chargingServiceId,
    },
  });

  const queryClient = useQueryClient();

  const { data: chargingService, isLoading: isChargingServiceLoading } =
    useChargingService(initialValues.chargingServiceId);

  const chargingServiceImageQueries = useChargingServiceImages(
    chargingService?.id,
    chargingService?.imageIds,
  );

  const isAnyImageLoading = chargingServiceImageQueries.some(
    (q) => q.isLoading,
  );

  const { mutateAsync: addChargingServiceImage } = useAddChargingServiceImage();
  const { mutateAsync: removeChargingServiceImage } =
    useRemoveChargingServiceImage();

  const handleChargingServiceImageRemoveClick = async (q) => {
    try {
      const removeChargingServiceImageRequest: RemoveChargingServiceImageRequest =
        {
          serviceId: chargingService.id,
          imageId: q.data.imageId.toString().replaceAll('/', '%2F'),
        };
      await removeChargingServiceImage(removeChargingServiceImageRequest);
      await queryClient.invalidateQueries({
        queryKey: ['chargingServices', chargingService.id],
        exact: false,
      });
    } catch (error) {
      notifyError('刪除失敗', error.message);
    }
  };

  const handleChargingServiceImageAddClick = async (files: FileWithPath[]) => {
    if (files.length === 0) return;

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const addChargingServiceImageRequest: AddChargingServiceImageRequest = {
          serviceId: chargingService.id,
          file: formData,
        };
        await addChargingServiceImage(addChargingServiceImageRequest);
        await queryClient.invalidateQueries({
          queryKey: ['chargingServices', chargingService.id],
          exact: false,
        });
      } catch (error) {
        notifyError('上傳失敗', error.message);
      }
    }
  };

  const handleSubmit = form.onSubmit((values) => {
    onSubmit(values);
  });

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  if (isChargingServiceLoading || isAnyImageLoading)
    return <LoadingOverlay visible={false} />;

  return (
    <>
      <form onSubmit={handleSubmit}>
        {chargingServiceImageQueries?.length ? (
          <Flex
            gap={9}
            style={{
              minHeight: 160,
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              overflowX: 'hidden',
              overflowY: 'auto',
              alignContent: 'flex-start',
            }}
          >
            {chargingServiceImageQueries?.map((q) => (
              <div
                key={q.data.imageId}
                style={{
                  width: 150,
                  height: 150,
                  borderRadius: 8,
                  position: 'relative',
                  overflow: 'hidden',
                  flexShrink: 0,
                }}
              >
                <Image
                  src={q.data.signedUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                {/* 刪除按鈕 */}
                <Button
                  size="xs"
                  variant="filled"
                  color="red"
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    padding: '2px 6px',
                    zIndex: 2,
                  }}
                  onClick={() => handleChargingServiceImageRemoveClick(q)}
                >
                  ×
                </Button>
              </div>
            ))}

            {/* 新增圖片按鈕 */}
            <div
              style={{
                width: 150,
                height: 150,
                borderRadius: 8,
                backgroundColor: '#F8F9FA',
                alignContent: 'center',
                flexShrink: 0,
                cursor: 'pointer',
              }}
            >
              <Dropzone
                onDrop={(files) => handleChargingServiceImageAddClick(files)}
                onReject={(files) => console.log('rejected files', files)}
                maxSize={5 * 1024 ** 2}
                accept={[MIME_TYPES.png]}
                style={{
                  width: '100%',
                  height: '100%',
                  border: '1px dashed var(--mantine-color-gray-4)',
                  borderRadius: 8,
                  padding: 0,
                }}
                styles={{
                  inner: {
                    height: '100%',
                  },
                }}
              >
                <Group justify="center" style={{ height: '100%' }}>
                  <Stack align="center">
                    <IconPlus size={24} color="#868E96" />
                    <Text
                      style={{
                        fontSize: 16,
                        fontWeight: 500,
                        color: '#868E96',
                      }}
                    >
                      新增圖片
                    </Text>
                  </Stack>
                </Group>
              </Dropzone>
            </div>
          </Flex>
        ) : (
          <Dropzone
            onDrop={(files) => handleChargingServiceImageAddClick(files)}
            onReject={(files) => console.log('rejected files', files)}
            maxSize={5 * 1024 ** 2}
            accept={[MIME_TYPES.png]}
            style={{
              border: '1px dashed var(--mantine-color-gray-4)',
              borderRadius: 8,
              padding: 0,
              height: 221,
            }}
          >
            <Group justify="center" gap="xl" mih={220}>
              <Stack align="center">
                <Dropzone.Accept>
                  <IconUpload size={52} stroke={1.5} />
                </Dropzone.Accept>
                <Dropzone.Reject>
                  <IconX size={52} stroke={1.5} />
                </Dropzone.Reject>
                <Dropzone.Idle>
                  <IconCloudUpload size={52} color="#212529" stroke={1.5} />
                </Dropzone.Idle>
                <div>
                  <Text size="lg" fw={500} inline mb={12} ta="center">
                    上傳圖片
                  </Text>
                  <Text size="sm" c="dimmed" inline mt={7} ta="center">
                    把 .png 檔案拖曳到這裡上傳,或是選擇圖片。
                  </Text>
                </div>
              </Stack>
            </Group>
          </Dropzone>
        )}

        <Group justify="flex-end" mt="24px">
          <Button variant="outline" onClick={handleCancel}>
            取消
          </Button>
          <Button
            type="submit"
            loading={isSubmitting}
            loaderProps={{ type: 'oval', size: 'xs' }}
          >
            {isUpdating ? '更新' : '下一步'}
          </Button>
        </Group>
      </form>
    </>
  );
}
